/**
 * Liabilities panel — bottom-right quadrant.
 * Shows liabilities by category with outstanding balance and monthly payment.
 */

import { useState } from 'react';
import type { Liability } from '../../types/cashflow';
import { formatCurrency } from '../../utils/cashflowCalc';

interface Props {
  liabilities: Liability[];
}

const CATEGORY_LABELS: Record<Liability['category'], string> = {
  mortgage: 'Mortgages',
  loan: 'Loans',
  credit_card: 'Credit Cards',
  other: 'Other',
};

const CATEGORY_ICONS: Record<Liability['category'], string> = {
  mortgage: '🏦',
  loan: '🚗',
  credit_card: '💳',
  other: '📋',
};

export default function LiabilityPanel({ liabilities }: Props) {
  const [collapsed, setCollapsed] = useState(false);

  const grouped = liabilities.reduce<Record<string, Liability[]>>((acc, l) => {
    if (!acc[l.category]) acc[l.category] = [];
    acc[l.category].push(l);
    return acc;
  }, {});

  const totalBalance = liabilities.reduce((sum, l) => sum + l.outstanding_balance, 0);
  const totalPayment = liabilities.reduce((sum, l) => sum + l.monthly_payment, 0);

  return (
    <div className="card" style={{ flex: 1, minWidth: 280 }}>
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-md)', cursor: 'pointer' }}
        onClick={() => setCollapsed((c) => !c)}
      >
        <h3 style={{ color: 'var(--color-error)', margin: 0, fontSize: '1rem', fontWeight: 700 }}>
          ▼ LIABILITIES
        </h3>
        <span style={{ color: 'var(--color-text-secondary)', fontSize: '1.1rem' }}>
          {formatCurrency(totalBalance)} {collapsed ? '▸' : '▾'}
        </span>
      </div>

      {!collapsed && (
        <div>
          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat} style={{ marginBottom: 'var(--spacing-md)' }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                {CATEGORY_ICONS[cat as Liability['category']]} {CATEGORY_LABELS[cat as Liability['category']]}
              </div>
              {items.map((l) => (
                <div key={l.name} style={{ padding: '6px 0', borderBottom: '1px solid var(--color-border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>{l.name}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginTop: 2 }}>
                        {l.interest_rate}% APR · {formatCurrency(l.monthly_payment)}/mo payment
                      </div>
                    </div>
                    <div style={{ fontWeight: 600, color: 'var(--color-error)', textAlign: 'right' }}>
                      {formatCurrency(l.outstanding_balance)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))}
          <div style={{ borderTop: '2px solid var(--color-border)', paddingTop: 'var(--spacing-sm)', marginTop: 'var(--spacing-sm)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
              <span>Total Owed</span>
              <span style={{ color: 'var(--color-error)' }}>{formatCurrency(totalBalance)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', marginTop: 4 }}>
              <span style={{ color: 'var(--color-text-secondary)' }}>Total Monthly Payments</span>
              <span>{formatCurrency(totalPayment)}/mo</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
